/**
 * useUnreadCount (050 US3) — the always-available, NON-SUSPENDING source for the
 * Viestit nav unread badge. `PrimaryNav` is mounted on every authenticated
 * route, so it cannot use the suspending `useCases` read; this hook fetches the
 * member's case list via `useEffect` and exposes the aggregate
 * `unreadConversationCount` from the 1.4.0 list envelope (`member.cases.list`),
 * defaulting to 0.
 *
 * Route-on-read refresh (FR-011): the fetch re-runs on every NAVIGATION (keyed on
 * the router `location`), so after a member OPENS a conversation (055 FR-004 marks it read in full,
 * server-side, via per-message `VastausLuettu`) and then moves — back to the
 * Viestit list, or to any other route — the badge picks up the upstream
 * decrement (`VastausLuettu` clears `hasUnreadReplies`, which the IS re-aggregates
 * into `unreadConversationCount`). The mark is fire-and-forget, so a navigation
 * that re-reads before it completes still sees the pre-mark count; the badge
 * converges on the next navigation after the mark lands (eventual, not
 * immediate). A same-member refetch does NOT reset the count
 * first, so the badge never flickers to 0-and-back mid-navigation; only an IDENTITY
 * change (sign out/in, account switch) or a non-member/absent-port state resets it.
 *
 * Honest by construction (P7): a non-member / signed-out / absent-port state, and
 * the from-scratch first read, resolve to 0 → the badge is hidden, never a
 * fabricated number. A same-member navigation refetch that resolves `unavailable`
 * or rejects keeps the LAST-KNOWN count (no flicker to 0-and-back on a transient
 * blip) rather than resetting — it never invents a number, only holds the last
 * real one. The count is the whole-inbox aggregate bounded by the IS 200-case
 * window (not a per-row sum — no N+1).
 */

import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { PortsContext } from '../providers/PortsContext.js';
import { useSession } from '../session/useSession.js';

/**
 * A stable key for the SIGNED-IN member (or a non-member sentinel). Included in
 * the fetch effect's deps so the count RE-FETCHES on an identity change (sign
 * out/in, account switch) rather than showing a prior member's unread count.
 */
function memberIdentityKey(state: ReturnType<typeof useSession>['state']): string {
  return state.kind === 'member' ? `member:${state.email}` : `non-member:${state.kind}`;
}

export function useUnreadCount(): number {
  const ports = useContext(PortsContext);
  const port = ports?.memberCases;
  const { state } = useSession();
  const identityKey = memberIdentityKey(state);
  // `location` is the NAVIGATION re-fetch trigger (route-on-read): its `key`
  // changes on every navigation, so reading replies then moving refreshes the badge.
  const location = useLocation();
  const [count, setCount] = useState(0);
  // Tracks the identity the current count belongs to, so a same-member navigation
  // refetch does NOT reset to 0 first (no flicker) while an identity CHANGE does.
  const shownIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    // `location.key` is a NAVIGATION re-fetch trigger only — the fetch reads the
    // cookie session, not the location — so reference it here to make the dependency
    // explicit (biome useExhaustiveDependencies), mirroring the identity trigger.
    void location.key;
    const identityChanged = shownIdentityRef.current !== identityKey;
    shownIdentityRef.current = identityKey;
    if (port === undefined || state.kind !== 'member') {
      // Non-member / signed-out / no port: no count at all.
      setCount(0);
      return;
    }
    // Reset ONLY on an identity change so a prior member's count never lingers
    // while the fresh read is in flight. A same-member navigation refetch keeps
    // the current count until the new read arrives (no 0-and-back flicker).
    if (identityChanged) {
      setCount(0);
    }
    let cancelled = false;
    void Promise.resolve(port.getCases())
      .then((view) => {
        if (!cancelled && view.status === 'ok') {
          setCount(view.unreadConversationCount ?? 0);
        }
      })
      .catch(() => {
        // Honest: on any read failure leave the count unchanged/hidden rather
        // than surface a fabricated number.
      });
    return () => {
      cancelled = true;
    };
    // identityKey → account switch; location.key → every navigation (route-on-read
    // refresh, FR-011); port → composition swap. (state.kind derives from identityKey.)
  }, [port, identityKey, state.kind, location.key]);

  return count;
}
